<template>
  <div class="modal-dialog modal-dialog-centered" role="document">
    <div class="modal-content">
      <div class="modal-header">
        <p class="modal-title">
          <i class="fas fa-tag mr-2"></i>
          <span class="font-weight-bold" v-if="isNew">Add RepCode</span>
          <span class="font-weight-bold" v-else>Edit RepCode Name — <span v-text="repcode.label"></span></span>
        </p>
        <button type="button" class="close" v-on:click="close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="modal-body">
        <small class="form-text text-muted mb-3" v-if="isNew">
          These billing values become the first-choice default when a contract is created for a household whose accounts share this RepCode.
        </small>
        <small class="form-text text-muted mb-3" v-else>
          Update the optional name to help identify this RepCode.
        </small>
        <div class="alert alert-info" role="alert" v-if="!isNew">
          <i class="fas fa-info-circle mr-2"></i>
          <strong>Billing defaults are read-only.</strong> Contact SmartKX support to modify billing settings.
        </div>
        <div class="form-group" v-if="isNew">
          <label>RepCode</label>
          <input
            type="text"
            class="form-control form-control-sm"
            placeholder="e.g. ABC123"
            v-model="form.code"
            :disabled="form.pending"
          />
        </div>
        <div class="form-group">
          <label>Name <small class="text-muted">(optional)</small></label>
          <input
            type="text"
            class="form-control form-control-sm"
            placeholder="e.g. John Smith"
            v-model="form.name"
            :disabled="form.pending"
          />
        </div>
        <div class="form-group" v-if="isNew">
          <label>Default Billing</label>
          <select v-model="form.value" class="form-control form-control-sm" :disabled="!isNew">
            <option v-for="(o, i) in options" :key="i" :value="o.value">{{ o.label }}</option>
          </select>
        </div>
        <div class="form-group" v-else>
          <label>Billing Defaults</label>
          <div class="form-control form-control-sm" style="background-color: #f5f5f5; cursor: default;">
            {{ getBillingDisplay(repcode.billing) }}
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-sm btn-secondary" v-on:click="close">{{ isNew ? 'Cancel' : 'Close' }}</button>
        <button type="button" class="btn btn-sm btn-primary" v-on:click="save" :disabled="form.pending" v-if="isNew">Save RepCode</button>
        <button type="button" class="btn btn-sm btn-primary" v-on:click="save" :disabled="form.pending" v-else>Save Name</button>
      </div>
    </div>
  </div>
</template>

<script>
import { Api, Modal } from "../../../../factory";
import { alert, session } from "../../../../service";

export default {
  name: `RepCodeBillingModal`,
  props: { data: { type: Object, required: true } },
  data() {
    return {
      repcode: this.data.repcode,
      form: { code: ``, name: ``, value: ``, pending: false },
    };
  },
  created() {
    var existing = this.repcode && this.repcode.billing;
    if (existing && existing.type && existing.schedule && existing.values) {
      this.form.value = JSON.stringify({
        schedule: existing.schedule,
        type: existing.type,
        values: existing.values,
      });
    }
    if (this.repcode && this.repcode.name) {
      this.form.name = this.repcode.name;
    }
  },
  computed: {
    isNew() {
      return !this.repcode;
    },
    hasExisting() {
      return !!(this.repcode && this.repcode.billing);
    },
    options() {
      return [
        { label: `-- Select Billing Type --`, value: `` },
        { label: `Quarterly Advance, Latest`, value: `{"schedule":"Quarterly","type":"advance","values":"Latest"}` },
        { label: `Quarterly Advance, Average Daily`, value: `{"schedule":"Quarterly","type":"advance","values":"Average Daily"}` },
        { label: `Quarterly Arrears, Latest`, value: `{"schedule":"Quarterly","type":"arrears","values":"Latest"}` },
        { label: `Quarterly Arrears, Average Daily`, value: `{"schedule":"Quarterly","type":"arrears","values":"Average Daily"}` },
        { label: `Quarterly Feb/May/Aug/Nov Advance, Latest`, value: `{"schedule":"Quarterly 2","type":"advance","values":"Latest"}` },
        { label: `Quarterly Feb/May/Aug/Nov Advance, Average Daily`, value: `{"schedule":"Quarterly 2","type":"advance","values":"Average Daily"}` },
        { label: `Quarterly Feb/May/Aug/Nov Arrears, Latest`, value: `{"schedule":"Quarterly 2","type":"arrears","values":"Latest"}` },
        { label: `Quarterly Feb/May/Aug/Nov Arrears, Average Daily`, value: `{"schedule":"Quarterly 2","type":"arrears","values":"Average Daily"}` },
        { label: `Monthly Advance, Latest`, value: `{"schedule":"Monthly","type":"advance","values":"Latest"}` },
        { label: `Monthly Arrears, Latest`, value: `{"schedule":"Monthly","type":"arrears","values":"Latest"}` },
        { label: `Monthly Arrears, Average Daily`, value: `{"schedule":"Monthly","type":"arrears","values":"Average Daily"}` },
      ];
    },
  },
  methods: {
    close() {
      Modal.close();
    },
    getBillingDisplay(billing) {
      if (billing && billing.schedule && billing.type && billing.values) {
        const typeDisplay = billing.type.charAt(0).toUpperCase() + billing.type.slice(1);
        return `${billing.schedule} ${typeDisplay}, ${billing.values}`;
      }
      return 'Not set';
    },
    async save() {
      // When editing existing repcode: only save the name
      if (!this.isNew) {
        this.form.pending = true;
        try {
          const firmId = session.get(`state`).firm._id;
          await Api.put(`repcodes/billing`, {
            firmId,
            code: this.repcode.code,
            name: this.form.name.trim() || null,
          });
          alert.message(`RepCode name saved.`, 1500);
          if (this.data.methods?.success) this.data.methods.success();
          else Modal.close();
        } catch (e) {
          console.error(e);
          alert.error(e.message || `Failed to save RepCode name.`);
        } finally {
          this.form.pending = false;
        }
        return;
      }

      // When creating new repcode: full save with billing
      this.form.pending = true;
      try {
        if (!this.form.code.trim()) {
          alert.error(`RepCode is required.`);
          this.form.pending = false;
          return;
        }
        var billing = this.form.value ? JSON.parse(this.form.value) : null;
        if (!billing) {
          alert.error(`Please select a billing type.`);
          this.form.pending = false;
          return;
        }
        
        // Check for duplicate RepCode in add mode
        const code = this.form.code.trim();
        const firmId = session.get(`state`).firm._id;
        try {
          const { data: existingRepCodes } = await Api.get(`repcodes?firmId=${firmId}`);
          if (existingRepCodes.some((r) => (r.code ?? "") === code)) {
            alert.error(`RepCode "${code}" already exists. Please use a different code.`);
            this.form.pending = false;
            return;
          }
        } catch (e) {
          console.warn("Could not check for duplicate RepCode", e);
        }
        
        await Api.put(`repcodes/billing`, { firmId, code, name: this.form.name.trim() || null, billing });
        alert.message(`RepCode added.`, 1500);
        if (this.data.methods?.success) this.data.methods.success();
        else Modal.close();
      } catch (e) {
        console.error(e);
        alert.error(e.message || `Failed to save RepCode billing.`);
      } finally {
        this.form.pending = false;
      }
    },
  },
};
</script>
